import { MatTableDataSource } from '@angular/material/table';

interface FilterableData {
    KpiDetail?: any[];
    subtable?: any;
}

export class FilterUtils {
    static applyCommonFilterLogic<T extends FilterableData>(
        data: T,
        filterList: string[],
        dataSource: MatTableDataSource<T>,
        auxFilterPredicate: (filter: string, data: T, isParent: boolean, showAll: boolean) => boolean,
        createMatSubtable: (data: T) => void,
        isShowAllEnabled: boolean
    ): boolean {

        const subRows = data.KpiDetail || [];
        const { parentFilters, childFilters } = this.categorizeFilters(filterList, dataSource.data, auxFilterPredicate, subRows);
        if (filterList.length > (parentFilters.length + childFilters.length)) return false;
        const parentPasses = parentFilters.length === 0 || this.checkParentFilters(parentFilters, data, dataSource.data, isShowAllEnabled);
        if (!parentPasses) return false;

        createMatSubtable(data);

        const childPasses = childFilters.length === 0 || this.checkChildFilters(childFilters, subRows, auxFilterPredicate, isShowAllEnabled);

        if (data.subtable?.filter !== undefined) {
            data.subtable.filter = childFilters.length === 0 ? '__FORCE__' : childFilters.join('+');
        }

        return childPasses;
    }

    private static categorizeFilters<T extends FilterableData>(
        filterList: string[],
        allData: T[],
        auxFilterPredicate: (filter: string, data: T, isParent: boolean, showAll: boolean) => boolean,
        currentSubRows: any[]
    ) {
        const parentFilters: string[] = [];
        const childFilters: string[] = [];

        for (const filter of filterList) {
            const existsInParent = allData.some(parent => auxFilterPredicate(filter, parent, true, true));
            const existsInChild = allData.some(parent => 
                (parent.KpiDetail || []).some(child => auxFilterPredicate(filter, child, false, true))
            );
            const existsInCurrentChildren = currentSubRows.some(child => auxFilterPredicate(filter, child, false, true));

            if (existsInParent && existsInChild) {
                if (existsInCurrentChildren) {
                    childFilters.push(filter);
                } else {
                    parentFilters.push(filter);
                }
            } else if (existsInParent) {
                parentFilters.push(filter);
            } else if (existsInChild) {
                childFilters.push(filter);
            }
        }

        return { parentFilters, childFilters };
    }

    private static checkParentFilters<T>(parentFilters: string[], data: T, allData: T[], isShowAllEnabled: boolean): boolean {
        const filterGroups = this.groupFiltersByProperty(parentFilters, allData);
        
        return Array.from(filterGroups.entries()).every(([property, filters]) => {
          return property === 'KpiName'
            ? filters.every(filter => this.matchesProperty(filter, data, property))
            : filters.some(filter => this.matchesProperty(filter, data, property));
        });
    }

    private static checkChildFilters<T>(
        childFilters: string[],
        subRows: any[],
        auxFilterPredicate: (filter: string, data: T, isParent: boolean, showAll: boolean) => boolean,
        isShowAllEnabled: boolean
    ): boolean {
        return subRows.some(sub => 
            childFilters.every(filter => auxFilterPredicate(filter, sub, false, isShowAllEnabled))
        );
    }

    private static groupFiltersByProperty<T>(filters: string[], allData: T[]): Map<string, string[]> {
        const filterGroups = new Map<string, string[]>();
        
        for (const filter of filters) {
            const matchingProperties = this.getMatchingProperties(filter, allData);
            const primaryProperty = matchingProperties[0] || 'unknown';
            
            if (!filterGroups.has(primaryProperty)) {
                filterGroups.set(primaryProperty, []);
            }
            filterGroups.get(primaryProperty)!.push(filter);
        }
        
        return filterGroups;
    }

    private static getMatchingProperties<T>(filter: string, allData: T[]): string[] {
        const filterLower = filter.toLowerCase();
        const propertyMatches = new Map<string, number>();
        
        const allowedProperties = ['KpiName', 'AccountID'];
        
        allData.forEach(item => {
            Object.entries(item as any).forEach(([key, value]) => {
                if (allowedProperties.includes(key) && 
                    typeof value === 'string' && 
                    value.toLowerCase().includes(filterLower)) {
                    propertyMatches.set(key, (propertyMatches.get(key) || 0) + 1);
                }
            });
        });
        
        return Array.from(propertyMatches.entries())
            .sort(([,a], [,b]) => b - a)
            .map(([key]) => key);
    }

    private static matchesProperty<T>(filter: string, data: T, property: string): boolean {
        const value = (data as any)[property];
        return typeof value === 'string' && value.toLowerCase().includes(filter.toLowerCase());
    }
}